import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../Toast';
import './MyPromptsModal.css';

export const PROMPT_BODY_MAX_LENGTH = 4000;
export const PROMPT_TITLE_MAX_LENGTH = 60;

/**
 * 내 프롬프트 목록 + 추가/수정/삭제 본체.
 *
 * 채팅의 + 칩 모달(MyPromptsModal)과 AI 맞춤 설정 허브가 같은 부품을 쓴다.
 * - `resetKey`가 바뀔 때마다 편집 상태를 초기화한다 (모달 열림, 허브 카테고리 전환).
 * - 저장한 게 없으면 추가 폼을 바로 펼친다 (빈 화면에서 한 번 더 누르게 하지 않기).
 */
const MyPromptsPanel = ({
  prompts,
  onCreate,
  onUpdate,
  onDelete,
  resetKey,
  createdMessage = '프롬프트를 저장했어요 · 이 카테고리 칩에서 바로 쓸 수 있어요',
  autoFocusBody = true,
}) => {
  const { showToast } = useToast();

  const [isAdding, setIsAdding] = useState(prompts.length === 0);
  const [newBody, setNewBody] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // 사용자가 직접 "+ 새 프롬프트"를 눌러 폼을 연 경우에만 true.
  // 빈 상태에서 자동으로 열린 폼은 목록이 뒤늦게 로드되면(허브 첫 진입) 다시 접는다.
  const userOpenedAddRef = useRef(false);

  const isEmpty = prompts.length === 0;

  useEffect(() => {
    userOpenedAddRef.current = false;
    setIsAdding(prompts.length === 0);
    setNewBody('');
    setNewTitle('');
    setEditingId(null);
    setDeletingId(null);
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    if (prompts.length > 0 && !userOpenedAddRef.current && !newBody && !newTitle) {
      setIsAdding(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts.length]);

  const handleCreate = async () => {
    const body = newBody.trim();
    if (!body || isSaving) return;
    setIsSaving(true);
    try {
      await onCreate({ body, title: newTitle.trim() || undefined });
      setNewBody('');
      setNewTitle('');
      setIsAdding(false);
      userOpenedAddRef.current = false;
      showToast(createdMessage, 'success');
    } catch (error) {
      showToast(error?.message || '프롬프트를 저장하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (prompt) => {
    setEditingId(prompt.id);
    setEditTitle(prompt.title || '');
    setEditBody(prompt.body || '');
    setDeletingId(null);
  };

  const handleUpdate = async () => {
    const body = editBody.trim();
    if (!body || !editingId || isSaving) return;
    setIsSaving(true);
    try {
      await onUpdate(editingId, { title: editTitle.trim(), body });
      setEditingId(null);
      showToast('프롬프트를 수정했어요', 'success');
    } catch (error) {
      showToast(error?.message || '프롬프트를 수정하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (promptId) => {
    setDeletingId(null);
    try {
      await onDelete(promptId);
      showToast('프롬프트를 삭제했어요', 'success');
    } catch (error) {
      showToast(error?.message || '프롬프트를 삭제하지 못했어요. 다시 시도해주세요.', 'error');
    }
  };

  return (
    <div className="myprompts__content">
      {isAdding ? (
        <div className="myprompts__form">
          <textarea
            autoFocus={autoFocusBody && !isEmpty}
            className="myprompts__form-body"
            maxLength={PROMPT_BODY_MAX_LENGTH}
            onChange={(event) => setNewBody(event.target.value)}
            placeholder={'자주 쓰는 요청을 붙여넣거나 입력하세요\n예: [단원명]으로 서술형 5문항 학습지를 만들어주세요'}
            rows={4}
            value={newBody}
          />
          <input
            className="myprompts__form-title"
            maxLength={PROMPT_TITLE_MAX_LENGTH}
            onChange={(event) => setNewTitle(event.target.value)}
            placeholder="칩에 보일 이름 (비우면 첫 줄로 자동)"
            type="text"
            value={newTitle}
          />
          <div className="myprompts__form-actions">
            {!isEmpty && (
              <button
                className="myprompts__btn-ghost"
                onClick={() => {
                  userOpenedAddRef.current = false;
                  setIsAdding(false);
                }}
                type="button"
              >
                취소
              </button>
            )}
            <button
              className="myprompts__btn-primary"
              disabled={!newBody.trim() || isSaving}
              onClick={handleCreate}
              type="button"
            >
              {isSaving ? '저장 중…' : '저장하기'}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="myprompts__add-btn"
          onClick={() => {
            userOpenedAddRef.current = true;
            setIsAdding(true);
          }}
          type="button"
        >
          + 새 프롬프트
        </button>
      )}

      {isEmpty && !isAdding && (
        <p className="myprompts__empty">아직 저장한 프롬프트가 없어요</p>
      )}

      <ul className="myprompts__list">
        {prompts.map((prompt) => (
          <li className="myprompts__item" key={prompt.id}>
            {editingId === prompt.id ? (
              <div className="myprompts__form myprompts__form--edit">
                <input
                  className="myprompts__form-title"
                  maxLength={PROMPT_TITLE_MAX_LENGTH}
                  onChange={(event) => setEditTitle(event.target.value)}
                  placeholder="칩에 보일 이름"
                  type="text"
                  value={editTitle}
                />
                <textarea
                  className="myprompts__form-body"
                  maxLength={PROMPT_BODY_MAX_LENGTH}
                  onChange={(event) => setEditBody(event.target.value)}
                  rows={4}
                  value={editBody}
                />
                <div className="myprompts__form-actions">
                  <button
                    className="myprompts__btn-ghost"
                    onClick={() => setEditingId(null)}
                    type="button"
                  >
                    취소
                  </button>
                  <button
                    className="myprompts__btn-primary"
                    disabled={!editBody.trim() || isSaving}
                    onClick={handleUpdate}
                    type="button"
                  >
                    {isSaving ? '저장 중…' : '저장하기'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="myprompts__item-main">
                  <strong className="myprompts__item-title">{prompt.title}</strong>
                  <p className="myprompts__item-body">{prompt.body}</p>
                </div>
                {deletingId === prompt.id ? (
                  <div className="myprompts__item-actions">
                    <button
                      className="myprompts__btn-danger"
                      onClick={() => handleDelete(prompt.id)}
                      type="button"
                    >
                      삭제하기
                    </button>
                    <button
                      className="myprompts__btn-ghost"
                      onClick={() => setDeletingId(null)}
                      type="button"
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <div className="myprompts__item-actions">
                    <button
                      aria-label={`${prompt.title} 수정`}
                      className="myprompts__btn-ghost"
                      onClick={() => startEdit(prompt)}
                      type="button"
                    >
                      수정
                    </button>
                    <button
                      aria-label={`${prompt.title} 삭제`}
                      className="myprompts__btn-ghost"
                      onClick={() => setDeletingId(prompt.id)}
                      type="button"
                    >
                      삭제
                    </button>
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MyPromptsPanel;
